// Copyright (c) 2025 Digital Asset (Switzerland) GmbH and/or its affiliates. All rights reserved.
// SPDX-License-Identifier: Apache-2.0
import { GridColDef } from '@mui/x-data-grid';
import { WithdrawAttestation } from '@daml.js/utility-bridge-v0/lib/Utility/Bridge/V0/Attestation/Withdraw/module';
import Loading from '../../../components/Loading';
import { StateCell, Status } from '../../../components/StateCell';
import { DataTable } from '../../../components/Table/DataTable';
import useBridgeAgreement from '../../../hooks/queries/bridge/agreement/useBridgeAgreement';
import useBridgeUserAgreement from '../../../hooks/queries/bridge/agreement/useBridgeUserAgreement';
import useWithdrawalAttestations from '../../../hooks/queries/bridge/withdrawal/useWithdrawAttestations';
import { fmt } from '../../../utils/common';
import useAcceptedWithdrawAttestations from '../../../hooks/queries/bridge/withdrawal/useAcceptedWithdrawAttestations';
import { destinationDomains } from './BurnAndAttest';

type Row = {
  contractId: string;
  state: string;
  payload: WithdrawAttestation;
  reason?: string;
  traceId: string;
};

const createHeaders: () => GridColDef<Row>[] = () => {
  return [
    {
      field: 'state',
      headerName: 'State',
      renderCell: (params) => (
        <StateCell state={params.row.state as Status} reason={params.row.reason} />
      ),
      flex: 100,
      filterable: false,
    },
    {
      field: 'amount',
      headerName: 'Amount',
      valueGetter: (params) => fmt(params.row.payload.amount, 6),
      flex: 100,
      filterable: false,
    },
    {
      field: 'destinationDomain',
      headerName: 'Destination Chain',
      valueGetter: (params) => {
        const domain = destinationDomains.find(d => d.value === params.row.payload.destinationDomain);
        return domain ? domain.display : params.row.payload.destinationDomain;
      },
      flex: 100,
      filterable: false,
    },
    {
      field: 'destinationAddress',
      headerName: 'Destination Address',
      valueGetter: (params) => params.row.payload.destinationRecipient,
      flex: 200,
      filterable: false,
    },
    {
      field: 'reference',
      headerName: 'Reference',
      valueGetter: (params) => params.row.payload.reference,
      flex: 100,
      filterable: false,
    },
    {
      field: 'contractId',
      headerName: 'Contract Id',
      valueGetter: (params) => params.row.contractId,
      flex: 150,
      filterable: false,
    },
    {
      field: 'traceId',
      headerName: 'Destination Transaction Id',
      valueGetter: (params) => params.row.traceId,
      flex: 150,
      filterable: false,
    },
  ];
};

export const Withdrawals = () => {
  const bridgeAgreement = useBridgeAgreement();
  const bridgeUserAgreement = useBridgeUserAgreement();
  const withdrawalAttestations = useWithdrawalAttestations();
  const acceptedWithdrawAttestations = useAcceptedWithdrawAttestations();

  const isLoading =
    bridgeAgreement.isLoading ||
    bridgeUserAgreement.isLoading ||
    withdrawalAttestations.isLoading ||
    acceptedWithdrawAttestations.isLoading ||
    !withdrawalAttestations.data ||
    !acceptedWithdrawAttestations.data;
  if (isLoading) return <Loading />;

  const rows = withdrawalAttestations.data
    .map((r) => ({
      contractId: r.contractId as string,
      state: 'Withdrawn',
      payload: r.payload,
      traceId: '',
    }))
    .concat(
      acceptedWithdrawAttestations.data.map((r) => ({
        contractId: r.contractId as string,
        state: 'Finalized',
        payload: r.payload.attestation,
        traceId: r.payload.traceId,
      })),
    );

  return (
    <>
      <DataTable
        title="Withdrawals"
        enableTableWrapper
        variant="h3"
        columns={createHeaders()}
        rows={rows}
        rowKey={(a) => a.contractId}
      />
    </>
  );
};
